import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  Slide,
  Typography,
} from '@material-ui/core';
import { toast } from 'react-toastify';
import useStyles from './styles';

import getValidationErrors, { IErrors } from '../../utils/getValidationErrors';

import BarberShopTitle from '../../components/BarberShopTitle';
import CustomForm from '../../components/CustomForm';
import { form, initialData, schema } from './form';
import companyService from '../../services/companyService';
import { useGetCategories } from '../../hooks/data/categories';
import { useAuth } from '../../hooks/auth';
import LoadingButton from '../../components/LoadingButton';

interface SignUpFormData {
  name: string;
  whatsapp: string;
  category: string;
}

const SignUpCompany: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const { signOut } = useAuth();

  const categoriesQuery = useGetCategories();

  const [errors, setErrors] = useState<IErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [acceptWhatsapp, setAcceptWhatsapp] = useState(false);
  const [openWhatsappDialog, setOpenWhatsappDialog] = useState(false);

  const [data, setData] = useState<SignUpFormData>(initialData);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!acceptWhatsapp) {
        setErrors({
          ...errors,
          acceptWhatsapp: 'Campo acima é obrigatório',
        });
        return;
      }
      setIsLoading(true);
      try {
        await schema.validate(data, {
          abortEarly: false,
        });

        await companyService.create({
          company_name: data.name,
          cellphone: data.whatsapp,
          category: [data.category],
        });

        // toast.success('Cadastro realizado com sucesso');
        history.push('/cadastro/planos');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorsValidate = getValidationErrors(err);

          setErrors(errorsValidate);

          return;
        }

        toast.error('Ocorreu ao realizar o cadastro. Tente novamente');
      } finally {
        setIsLoading(false);
      }
    },
    [acceptWhatsapp, data, errors, history],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        [e.target.name]:
          e.target.name === 'whatsapp'
            ? e.target.value.replace(/\D+/g, '')
            : e.target.value,
      });
      setErrors({
        ...errors,
        [e.target.name]: '',
      });
    },
    [data, errors],
  );

  const categoriesOptions = useMemo(() => {
    if (categoriesQuery.status !== 'success') {
      return [];
    }

    return categoriesQuery.data.map(category => ({
      value: category.id,
      label: category.name,
    }));
  }, [categoriesQuery.data, categoriesQuery.status]);

  const handleLogout = useCallback(() => {
    signOut();
    history.push('/');
  }, [history, signOut]);

  return (
    <Box display="flex" justifyContent="center" maxWidth="600px" margin="auto">
      <Slide in direction="left">
        <Box
          p={3}
          display="flex"
          flexDirection="column"
          width="100%"
          alignItems="center"
          textAlign="center"
          className={classes.root}
        >
          <BarberShopTitle title="Agendfly" />
          <Typography variant="h5" className={classes.title}>
            Cadastre seu negócio
          </Typography>
          <form onSubmit={handleSubmit} className={classes.form}>
            <CustomForm
              onChange={handleChange}
              data={data}
              form={form.map(formSection => ({
                ...formSection,
                items: formSection.items.map(formItem => {
                  if (formItem.name !== 'category') {
                    return formItem;
                  }

                  return {
                    ...formItem,
                    options: categoriesOptions,
                  };
                }),
              }))}
              errors={errors}
            />
            <FormControlLabel
              style={{ marginTop: '8px' }}
              control={
                // eslint-disable-next-line react/jsx-wrap-multilines
                <Checkbox
                  checked={acceptWhatsapp}
                  onChange={e => setAcceptWhatsapp(e.target.checked)}
                  color="primary"
                />
              }
              // eslint-disable-next-line prettier/prettier
              label={(
                <Typography>
                  Aceito a publicação das informações acima
                  <Button
                    variant="text"
                    style={{ textTransform: 'initial', display: 'inline' }}
                    onClick={() => setOpenWhatsappDialog(true)}
                    color="primary"
                  >
                    Saiba mais
                  </Button>
                </Typography>
                // eslint-disable-next-line prettier/prettier
              )}
            />
            {errors.acceptWhatsapp && (
              <Typography color="error">{errors.acceptWhatsapp}</Typography>
            )}
            <LoadingButton
              loading={isLoading}
              variant="contained"
              color="primary"
              className={classes.submitButton}
              type="submit"
              size="large"
              fullWidth
            >
              Cadastrar
            </LoadingButton>
            <Button
              disabled={isLoading}
              variant="outlined"
              color="primary"
              className={classes.submitButton}
              type="button"
              size="large"
              fullWidth
              onClick={handleLogout}
            >
              Cancelar
            </Button>
          </form>
          <Dialog
            onClose={() => setOpenWhatsappDialog(false)}
            aria-labelledby="simple-dialog-title"
            open={openWhatsappDialog}
          >
            <DialogContent>
              <DialogContentText color="textPrimary">
                Ao final de um agendamento, será disponibilizado ao cliente um
                link para envio de confirmação do agendamento via whatsapp. O
                whatsapp comercial será utilizado para criar o link de envio da
                mensagem.
                <br />
                Não se preocupe, suas informações não serão utilizadas para
                outros fins por nossa plataforma.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenWhatsappDialog(false)}
                color="primary"
                autoFocus
              >
                Entendi
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Slide>
    </Box>
  );
};

export default SignUpCompany;
